import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import Env from '../../../util/env';
import Utility from '../../../util/utilityfunctions';
import {currentResource} from '../../../util/utils';
import {maxLengthInputs, serviceURL} from '../../../util/uiconstants';
import {ENGAGEMENTS, PROJECT_NAME} from './utils/constants';
import normalizePayload from './utils/normalizePayload';

import selectPrimaryOrRegionalEngagements from '../../../reducers/Clients/selectors/selectPrimaryOrRegionalEngagements';
import selectAllClients from '../../../reducers/Clients/selectors/selectAllClients';
import selectUserData from '../../../actions/UserPreferences/selectors/selectUserData';
import {getAllClients} from '../../../actions/Clients/clientsaction';
import {clearProject, createProject} from '../../../actions/Project/projectcreationactions';

import Form from '@ey/canvascoreservices/Form';
import OutputText from '@ey/canvascoreservices/OutputText';

import TemplateFullPage from '../../Templates/TemplateFullPage';
import ProgressLoader from '../../CoreWrappers/ProgressLoader';
import StyledOutputTextWithLink from './StyledOutputTextWithLink';
import StyledStep from './StyledStep';
import ProjectName from './ProjectName';
import Engagements from './Engagements';
import Controls from './Controls';
import TextLink from '../../CoreWrappers/TextLink';

const CreateProjectGRA = () => {
	const [isLoading, setIsLoading] = useState(false);

	const labels = currentResource.createGRAProjectLabels;

	const engagements = useSelector(selectPrimaryOrRegionalEngagements);
	const projectOwner = useSelector(selectUserData);
	const clients = useSelector(selectAllClients);

	const dispatch = useDispatch();

	const handleGetAllClients = () => dispatch(getAllClients(true));

	useEffect(() => {
		if (!engagements) handleGetAllClients();
	}, []);

	const initialValues = {
		[PROJECT_NAME]: '',
		[ENGAGEMENTS]: []
	};

	const schema = {
		[PROJECT_NAME]: {required: true, maxLength: maxLengthInputs.projectName},
		[ENGAGEMENTS]: {required: true, requiredMessage: labels.engagementsRequiredMessage}
	};

	const renderHeader = (label) => {
		return <OutputText h6>{label}</OutputText>;
	};

	const renderLinkTrigger = (label, href, target) => {
		const props = {
			label,
			href,
			target
		};

		return <TextLink {...props} />;
	};

	const renderInstructional = () => {
		const props = {className: 'instructional'};

		const messageParts = labels.creationInstructional.split('{link}');

		return <StyledOutputTextWithLink {...props}>
			<div className="message">
				<OutputText body1 light>
					{Utility.createRichTextElement(messageParts[0])}
				</OutputText>
				{renderLinkTrigger(labels.userGuideLinkText, Env.getURL('Get_Clients_Access_Url'), '_blank')}
				<OutputText body1 light>
					{Utility.createRichTextElement(messageParts[1])}
				</OutputText>
			</div>
		</StyledOutputTextWithLink>;
	};

	const props = {
		initialValues,
		schema,
		labels: currentResource.formLabels,
		onSubmit: (values, callback) => {
			const selectedClientCodes = Array.from(
				new Set(engagements.filter(({id}) => values[ENGAGEMENTS].includes(id)).map(({clientName}) => clientName))
			);

			const selectedClients = clients
				.filter(({clientName}) => selectedClientCodes.includes(clientName))
				.map(({clientCode, clientName, sources}) => ({
					clientCode,
					name: clientName,
					source: sources[0]
				}));

			const payload = normalizePayload(values, projectOwner, engagements, selectedClients);

			const projectApiUrl = `${Env.getBaseURL(serviceURL)}Projects`;
			setIsLoading(true);
			dispatch(createProject(projectApiUrl, payload))
				.then(() => {
					dispatch(clearProject()).then(() => {
						callback();
					});
				})
				.catch(() => {
					setIsLoading(false);
				});
		}
	};

	return (
		<TemplateFullPage includeScroll={false}>
			{isLoading && <ProgressLoader />}
			<Form {...props}>
				{({values, errors, handleChange, handleSubmit, handleReset, isFormValid}) => {
					const formProps = {values, errors, handleChange, handleSubmit, handleReset, isFormValid, labels};
					const sharedProps = {...formProps, renderHeader};

					return (
						<StyledStep>
							<div className="customScrollbar">
								<div>
									{renderInstructional()}
									<ProjectName {...sharedProps} />
									<Engagements {...sharedProps} engagements={engagements} />
								</div>
							</div>
							<Controls {...formProps} />
						</StyledStep>
					);
				}}
			</Form>
		</TemplateFullPage>
	);
};

export default CreateProjectGRA;
